import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaStore } from 'react-icons/fa';
import ApiImage from './ApiImage';
import useSelectedCommunity from '../../hooks/community/useSelectedCommunity';
import sponsorService from '../../services/sponsorService';
import Sponsor, { SponsorPlacement } from '../../types/sponsor';

type Props = {
  placement?: SponsorPlacement;
  eventId?: number;
  photoAlbumId?: string;
  scoreCardId?: number;
  taskId?: number;
  /** Hide the section title (useful inside modals). */
  hideTitle?: boolean;
  className?: string;
};

function SponsorCard({ sponsor, onClick }: { sponsor: Sponsor; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-36 flex-shrink-0 flex-col items-center gap-2 text-center transition-opacity hover:opacity-90">
      <div className="flex h-20 w-36 items-center justify-center overflow-hidden rounded-xl bg-secondary-200">
        {sponsor.logo ? (
          <ApiImage src={sponsor.logo} alt={sponsor.name} className="h-full w-full object-cover" />
        ) : (
          <FaStore className="h-7 w-7 text-primary/50" />
        )}
      </div>
      <span className="line-clamp-2 w-full text-xs font-medium text-primary">{sponsor.name}</span>
    </button>
  );
}

/**
 * Horizontal sponsor strip. Fetches by placement or entity id; hides when empty.
 * Tracks impressions on load and clicks when opening the detail page.
 */
export default function HorizontalSponsorsList({
  placement,
  eventId,
  photoAlbumId,
  scoreCardId,
  taskId,
  hideTitle = false,
  className,
}: Props): JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const community = useSelectedCommunity();
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const trackedImpressions = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!community?.id) {
      setSponsors([]);
      return undefined;
    }

    let cancelled = false;
    setSponsors([]);
    sponsorService
      .getCommunitySponsors(community.id, {
        placement,
        eventId,
        photoAlbumId,
        scoreCardId,
        taskId,
      })
      .then((data) => {
        if (!cancelled) setSponsors(data);
      })
      .catch(() => {
        if (!cancelled) setSponsors([]);
      });

    return () => {
      cancelled = true;
    };
  }, [community?.id, placement, eventId, photoAlbumId, scoreCardId, taskId]);

  useEffect(() => {
    if (!community?.id || sponsors.length === 0) return;

    sponsors.forEach((sponsor) => {
      if (trackedImpressions.current.has(sponsor.sponsorId)) return;
      trackedImpressions.current.add(sponsor.sponsorId);
      sponsorService.trackImpression(community.id, sponsor.sponsorId).catch(() => {
        /* fire-and-forget */
      });
    });
  }, [sponsors, community?.id]);

  if (sponsors.length === 0) {
    return null;
  }

  const handlePress = (sponsor: Sponsor) => {
    if (!community?.id) return;
    sponsorService.trackClick(community.id, sponsor.sponsorId).catch(() => {
      /* fire-and-forget */
    });
    navigate(`/community/sponsors/${sponsor.sponsorId}`, { state: { sponsor } });
  };

  return (
    <div className={className ?? 'flex flex-col gap-3'}>
      {!hideTitle && <h3 className="text-base font-semibold text-primary">{t('page.community.sponsors')}</h3>}
      <div className="flex gap-4 overflow-x-auto pb-1">
        {sponsors.map((sponsor) => (
          <SponsorCard key={sponsor.sponsorId} sponsor={sponsor} onClick={() => handlePress(sponsor)} />
        ))}
      </div>
    </div>
  );
}
